/**
 * The hub's ONE 3D model — route spelling, row shape and the three verbs every
 * surface uses (`t_hub_bim_model`, 2026-10-27).
 *
 * The model used to belong to a dashboard (`/dashboards/:id/bim-models…`). It is
 * the hub's now, so the routes are hub-level and the id is NOT part of any path:
 * one model per hub, replaced rather than appended. Keeping the spelling in one
 * module is what makes that true — the page, the upload modal, the scan attach
 * and the settings surface all read the same constants.
 *
 * The hub still lists rows (0 or 1) rather than answering a single object: the
 * served revision is "the newest row" (`newestBimModelRow`), and the live poll's
 * signature comparison already speaks that shape.
 */
import { apiFetch, apiFetchRaw } from './api';
import { bimModelDownloadName } from '@/utils/bimDashboardModels';
import { BIM_MODELS_PATH, bimModelFilePath } from '@/utils/bimModelRoutes';

export * from '@/utils/bimModelRoutes';

/** One row of `GET /api/bim-models` — the model the hub serves. */
export interface BimModelRow {
  id?: string;
  name?: string;
  s3Key?: string | null;
  fileSize?: number;
  createdAt?: string;
  updatedAt?: string;
  /** Presigned object URL (direct storage access); prefer `fileApiPath`. */
  modelUrl?: string;
  /** Stable hub path (`/api/bim-models/<id>/file`) — works on every transport. */
  fileApiPath?: string;
  [key: string]: unknown;
}

/** The hub's model rows (0 or 1). A refused read is the caller's to report. */
export async function fetchHubBimModels(): Promise<BimModelRow[]> {
  const rows = await apiFetch<BimModelRow[]>(BIM_MODELS_PATH);
  return Array.isArray(rows) ? rows : [];
}

/**
 * Replace the hub's model. The hub deletes the row it replaces (and the object
 * when nothing else references it), carries the rooms document onto the new row
 * and answers the new row.
 */
export async function uploadHubBimModel(file: File, name: string): Promise<BimModelRow | null> {
  const form = new FormData();
  form.append('bim_model', file);
  form.append('bim_model_name', name);
  const response = await apiFetchRaw(BIM_MODELS_PATH, { method: 'POST', body: form });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `Upload failed (${response.status})`);
  }
  return (await response.json()) as BimModelRow;
}

/** Remove the hub's model (and its rooms sidecar). Admin-only on the hub. */
export async function deleteHubBimModel(modelId: string): Promise<void> {
  await apiFetch(`${BIM_MODELS_PATH}/${modelId}`, { method: 'DELETE' });
}

/**
 * Save this row's bytes to the device under the row's own name.
 *
 * The route serves bytes with no file name, so the download name comes from the
 * row (`bimModelDownloadName`): a manager that saves `TorikDemoHouse-v2.5-site`
 * with no extension cannot open it.
 */
export async function downloadHubBimModel(row: BimModelRow): Promise<void> {
  const modelId = String(row.id ?? '');
  if (!modelId) throw new Error('Model id missing');
  const response = await apiFetchRaw(bimModelFilePath(modelId));
  if (!response.ok) throw new Error(`Download failed (${response.status})`);
  const blob = await response.blob();
  // The platform save is loaded when it is used: `useScanUpload` reaches the
  // Capacitor plugins and the scan API, which a Node import of this module must
  // not need (same reason `scanModelAttach` imports its transport lazily).
  const { saveScanFileToDevice } = await import('@/composables/useScanUpload');
  await saveScanFileToDevice({
    name: bimModelDownloadName(row.name, row.s3Key),
    blob,
    contentType: 'application/octet-stream',
  });
}
